import React, { useState, useEffect } from 'react';
import { Modal as ModalPopup } from 'react-bootstrap';
import { I18n } from '../../../helpers/i18n';
import client from '../../../helpers/client';
import mvpAuth from '../../../helpers/mvpAuth';
import RatingStarsDisplay from './RatingStarsDisplay';
import starActive from '../../../static/images/mvp/star_active.png';
import './RatingListModal.css';
import './BuddyRatingListModal.css';
import moment from 'moment';

function getAgeAndBirthYear(dob) {
  if (!dob) return null;
  if (typeof dob !== 'string' || dob.length < 4) return null;
  const parts = dob.split('/');
  const year = parts.length >= 3 ? parts[2] : dob.slice(-4);
  const dateObj = moment(dob, 'DD/MM/YYYY', true);
  const age = dateObj.isValid() ? moment().diff(dateObj, 'years') : null;
  return { age, year };
}

function maskReviewerName(name) {
  if (!name || typeof name !== 'string') return '?';
  const s = name.trim();
  if (s.length === 0) return '?';
  if (s.length === 1) return s;
  return s[0] + '***' + s[s.length - 1];
}

function BuddyRatingListModal({ visible, sessionId, userId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (visible && sessionId && userId) {
      setLoading(true);
      client.sendRequest({
        path: 'mvp/user-detail-by-session',
        method: 'POST',
        params: { sessionId, userId, role: 'guide' },
        headers: mvpAuth.getHeaderForMvp(),
      }).then(({ data: response }) => {
        setData(response);
      }).catch(() => {
        setData(null);
      }).finally(() => {
        setLoading(false);
      });
    }
  }, [visible, sessionId, userId]);

  const ratings = data?.listRatings || [];
  const avgRating = data?.avgRating ?? 0;
  const totalRating = data?.totalRating ?? 0;
  const buddyName = data?.name || I18n.t('buddy_ratings_title');
  const ageAndYear = getAgeAndBirthYear(data?.dob);
  const subtitle = ageAndYear
    ? I18n.t('buddy_modal_age_born_year', { age: ageAndYear.age ?? '?', year: ageAndYear.year })
    : null;

  return (
    <ModalPopup
      size="lg"
      show={visible}
      onHide={onClose}
      id="rating-list-modal"
      className="buddy-rating-modal"
      centered
      style={{ opacity: 1 }}
    >
      <div className="rating-list-modal-header">
        <div className="rating-list-modal-header-row">
          <span className="rating-list-modal-close" onClick={onClose}>✕</span>
          <span className="rating-list-modal-header-title">{I18n.t('account_type_selection_senior')} {buddyName}</span>
        </div>
        {subtitle && (
          <div className="rating-list-modal-header-subtitle">{subtitle}</div>
        )}
      </div>
      <ModalPopup.Body>
        <div className="rating-list-modal-content">
          {totalRating > 0 && (
            <div className="rating-list-summary">
              <img src={starActive} className="rating-list-summary-star-image" alt="" />
              <span className="rating-list-summary-score">{avgRating}</span>
              <span className="rating-list-summary-separator">•</span>
              <span className="rating-list-summary-count">{totalRating.toLocaleString()} {I18n.t('ratings_count')}</span>
            </div>
          )}

          {loading ? (
            <div className="rating-list-loading">...</div>
          ) : ratings.length === 0 ? (
            <div className="rating-list-empty">{I18n.t('no_rating')}</div>
          ) : (
            <div className="rating-list-items">
              {ratings.map((item, index) => {
                const evaluation = item.evaluation || item;
                return (
                  <div key={index} className="rating-list-item">
                    <div className="rating-list-item-header">
                      <div className="rating-list-item-avatar-wrapper">
                        <div className="rating-list-item-avatar">
                          {(item.name || '?')[0].toUpperCase()}
                        </div>
                        <span className="rating-list-item-role">{I18n.t('account_type_selection_senior')}</span>
                      </div>
                      <div className="rating-list-item-info">
                        <span className="rating-list-item-name">{maskReviewerName(item.name)}</span>
                        <div className="rating-list-item-meta">
                          <RatingStarsDisplay
                            score={evaluation.score}
                            size={14}
                            activeColor="#F59E0B"
                            inactiveColor="#4B5563"
                          />
                          {evaluation.time && (
                            <span className="rating-list-item-date">
                              {moment(evaluation.time).format('DD-MM-YYYY')}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    {evaluation.sessionTitle && (
                      <p className="rating-list-item-session-line">
                        {I18n.t('session_label')}: {evaluation.sessionTitle}
                      </p>
                    )}
                    {evaluation.contentExtra && (
                      <p className="rating-list-item-comment">{evaluation.contentExtra}</p>
                    )}
                    {evaluation.content && (Array.isArray(evaluation.content) ? evaluation.content : [evaluation.content]).length > 0 && (
                      <div className="rating-list-item-tags">
                        {(Array.isArray(evaluation.content) ? evaluation.content : [evaluation.content]).map((tag, i) => (
                          <span key={i} className="rating-list-item-tag">{tag}</span>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </ModalPopup.Body>
    </ModalPopup>
  );
}

export default BuddyRatingListModal;
